
/*
 * Filename: tabbed-content.js
 * By: J. Nguyen
 * Date: 9/7/21
 *
 * Description: 
 * This template will be used for reusable site modules.  Be sure to only use vanilla JS.
 * All the variables and functions which are not exported are private within the module and cannot be used outside. 
 * Only the exported members are public and can be used by importing them.
 *

  DISPLAY MARKUP:
  <section class="tabbed-content">
    ...
      <nav class="tabs">
        <div class="prev-btn-container">
          <button type="button" class="prev-btn {disabled}">
        </div>
        <div class="tab-sliding-wrapper">
          <div class="tab active" data-index="0"> ... </div>
          <div class="tab" data-index="1"> ... </div>
          ...
        </div>
        <div class="next-btn-container">
          <button type="button" class="next-btn {disabled}">
        </div>
      </div>
    ...
      <div class="tab-contents">
        <div class="tab-content-container">
          <div class="tab-content active"> ... </div>
          <div class="tab-content"> ... </div>
          ...
        </div>
      </div> 
    ...
  </section>
 */


export default function TabbedContent(element, optionOverwrites) {

  const elem = element;                       // remember the DOM element that this object is bound to
  let api = {};                               // Object to contain the publicly accessible functions

  // default options
  const defaults = {
    debugMode: false,
    initDelay: 200,                // delay time to perform measurement calculations
    noWrap: true,                  // setting to keep tab text from wrapping
    resizeDelay: 200               // delay for resizing debouncing
  };

  // overwrite options based on optionOverwrites
  const options = Object.assign(defaults, optionOverwrites);


  // DOM NODES
  const tabContainer = elem.getElementsByClassName('tab-container')[0];
  const tabSlider = elem.getElementsByClassName('tab-sliding-wrapper')[0];
  const tabNodes = elem.getElementsByClassName('tab');
  const tabContentNodes = elem.getElementsByClassName('tab-content');

  const prevBtn = elem.getElementsByClassName('prev-btn')[0];
  const nextBtn = elem.getElementsByClassName('next-btn')[0];

  // DATA VARIABLES
  let resizeTimeout = null;                      // pointer for timeout id

  let activeTabIndex = 0;                         // tracker for which index tab value is active  
 
  let tabWidth = 0;                               // store the current tab width
  let sliderWidth = 0;
  let containerWidth = 0;
  let isFullyVisible = false;                     // determine if all tabs are fully visible

  let offsetArr = [];                             // array to store tab left edge offset
  let widthArr = [];                              // array to store tab widths

  let viewPortLeft = 0;                           // X-coordinate of tab viewport left edge
  let viewPortRight = 0;                          // X-coordinate of tab viewport right edge
  let offset = 0;                                 // live tracking of slider offset

  init();

  // initilize the module
  function init() {
    debug('=============== insider TabbedPanels init() ===============');

    // delay execution to ensure full render of dimensions
    setTimeout(initTabs, options.initDelay); 

    // Event handlers
    prevBtn.addEventListener('click', prevTab);
    nextBtn.addEventListener('click', nextTab);
    window.addEventListener('resize', resizeDebouncer);

    for (let x = 0; x < tabNodes.length; x++) {
      tabNodes[x].addEventListener('click', setActiveTabAndContent);
    }
  }


  // HELPER FUNCTIONS
  //===============================================
  
  // Basic debug function
  function debug(msg) { 
    if (defaults.debugMode) {
      console.log(msg);
    }
  }

  // PRIVATE FUNCTIONS
  //===============================================
  
  // This function will set the updated storage values for tabs, sliding wrapper, and containers.
  function setDimensions() {
    
    containerWidth = tabContainer.offsetWidth;
    sliderWidth = tabSlider.scrollWidth;
    
    if (containerWidth >= sliderWidth) {
      isFullyVisible = true;
    } else {
      isFullyVisible = false;
      viewPortLeft = 0;
      viewPortRight = viewPortLeft + containerWidth;
    }

    debug('containerWidth: ' + containerWidth);
    debug('sliderWidth: ' + sliderWidth);
    debug('isFullyVisible: ' + isFullyVisible);

  }

  // This function will set offsetArr and widthArr
  function setTrackingArrays() {
    if (!isFullyVisible) { 
      let offsetSum = 0;
      widthArr = [];
      offsetArr = [];

      offsetArr.push(offsetSum);

      for (let x = 0; x < tabNodes.length; x++) {
        widthArr.push( tabNodes[x].offsetWidth );
        offsetSum = offsetSum + tabNodes[x].offsetWidth;
        offsetArr.push(offsetSum);
      }

      debug('widthArr: ' + widthArr);
      debug('offsetArr: ' + offsetArr);
    }
  } 

  // This function will hide/show arrows depending on dimensions and activeTabIndex
  function hideShowArrows() {

    if (isFullyVisible) {

      prevBtn.classList.add('disabled');
      nextBtn.classList.add('disabled');

    } else {

      prevBtn.classList.remove('disabled');
      nextBtn.classList.remove('disabled');

      if (activeTabIndex == 0) {
        prevBtn.disabled = true;
      }

      if (activeTabIndex > 0) {
        prevBtn.disabled = false;
      } 

      if (activeTabIndex < (tabNodes.length - 1)){
        nextBtn.disabled = false;
      }

      if (activeTabIndex == (tabNodes.length - 1)){
        nextBtn.disabled = true;
      }
    }
  }

  // This function will clear all active tabs.
  function clearTabsAndContent() {
    for (let x = 0; x < tabNodes.length; x++) {
      tabNodes[x].classList.remove('active');
      tabContentNodes[x].classList.remove('active');
    }
  }

  // This function will animate the tab by an offset value.
  // Negative int values passed will slide the tabs to the left,
  // while positive int values will slide the tabs to the right.
  function animateTab(slideOffset) {
    debug('animateTab()');

    tabSlider.style.transform = "translateX(" + slideOffset + "px)";
  }


  // This function will determine if the sliding wrapper should 
  // move and if so by how much
  function determineMovement() {
    debug('determineMovement()');

    const tabWidth = widthArr[activeTabIndex];
    const activeTabLeft = offsetArr[activeTabIndex];
    const activeTabRight = offsetArr[activeTabIndex + 1];

    if (!isFullyVisible) {
      
      // active tab is obscured, slide left
      if (activeTabRight >= viewPortRight) {
        
        offset = activeTabRight - viewPortRight + offset;
        viewPortLeft = offset;
        viewPortRight = activeTabRight;
        animateTab( -1 * offset );

        debug('slide right');      

      // active tab is obscured, slide right
      } else if ((viewPortLeft >= activeTabLeft) && (viewPortLeft <= activeTabRight)) {
        
        offset = offset - (viewPortLeft - activeTabLeft);
        viewPortLeft = activeTabLeft;
        viewPortRight = viewPortLeft + containerWidth;
        animateTab( -1 * offset );

        debug('slide left');
      }

    } 

    debug('tabWidth: ' + tabWidth);
    debug('offset: ' + offset);
    debug('activeTabLeft: ' + activeTabLeft);
    debug('activeTabRight: ' + activeTabRight);
    debug('viewPortLeft: ' + viewPortLeft);
    debug('viewPortRight: ' + viewPortRight);
  }

  
  // PUBLIC FUNCTIONS
  //===============================================
  
  // This function will setup the tabbed-content module by updating state/tracking variables
  function initTabs() {
    setDimensions();
    hideShowArrows();
    setTrackingArrays();
  }

  // This function will debounce the resizeCallback
  function resizeDebouncer() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(resizeCallback, options.resizeDelay);
  }

  // This function will recalculate dimensions upon resize.
  function resizeCallback() {
    debug('=============== resizeCallback() fired ===============');

    // reset tabs and recalulate active tab
    animateTab(0);
    offset = 0;
    
    initTabs();
    determineMovement();   
    
  } 
  
  // This function will traverse the tabs to the previous tab
  function prevTab(event) {
    debug('prevTab()');

    if (activeTabIndex > 0) {
      activeTabIndex--;
      clearTabsAndContent();
      tabNodes[activeTabIndex].classList.add('active');
      tabContentNodes[activeTabIndex].classList.add('active');
      hideShowArrows();
      determineMovement();
    }

    debug('activeTabIndex: ' + activeTabIndex);
  }

  // This function will traverse the tabs to the next tab
  function nextTab(event) {
    debug('nextTab()');
    
    if (activeTabIndex < (tabNodes.length - 1)) {
      activeTabIndex++;
      clearTabsAndContent();
      tabNodes[activeTabIndex].classList.add('active');
      tabContentNodes[activeTabIndex].classList.add('active');
      hideShowArrows();
      determineMovement();
    }

    debug('activeTabIndex: ' + activeTabIndex);
  }

  // This function will set the active tab/content
  function setActiveTabAndContent(event) {
    debug('setActiveTab()');

    const el = event.target;
    const nearestTab = el.closest('.tab');

    clearTabsAndContent();
    nearestTab.classList.add('active');
    activeTabIndex = Number(nearestTab.dataset.index);
    tabContentNodes[activeTabIndex].classList.add('active');
    hideShowArrows();
    determineMovement();

    debug('activeTabIndex: ' + activeTabIndex);
  }

  
  // EXPOSE PUBLIC FUNCTIONS VIA THE API
  // Map all the functions we want to expose for public access
  //===============================================================
  api.initTabs = initTabs;
  api.resizeDebouncer = resizeDebouncer;
  api.resizeCallback = resizeCallback;
  api.prevTab = prevTab;
  api.nextTab = nextTab;
  api.setActiveTabAndContent = setActiveTabAndContent;
  return api;

}
